/**
 * QCDashboard Component
 *
 * Main dashboard with summary tiles and tabbed layout:
 * - Summary tiles (In Control, Warning, Out of Control, Pass Rate)
 * - Instruments tab (DataTable with search + pagination)
 * - Alerts tab (active violations + acknowledged table)
 *
 * Auto-refreshes every 5 minutes.
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from "react";
import {
  Loading,
  InlineNotification,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@carbon/react";
import { Renew, Download } from "@carbon/icons-react";
import { useIntl } from "react-intl";
import {
  getFromOpenElisServer,
  hasPermissionOrGlobalAdmin,
} from "../../utils/Utils";
import ActiveViolationsBanner from "./ActiveViolationsBanner";
import QCSummaryTiles from "./QCSummaryTiles";
import InstrumentsTab from "./InstrumentsTab";
import AlertsTab from "./AlertsTab";
import BenchQcTab from "./BenchQcTab";
import QCExportModal from "./QCExportModal";
import PageTitle from "../../common/PageTitle/PageTitle";
import PageBreadCrumb from "../../common/PageBreadCrumb";
import UserSessionDetailsContext from "../../../UserSessionDetailsContext";
import "./QCDashboard.css";

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

const QCDashboard = ({ initialTab = 0 }) => {
  const intl = useIntl();
  const intlRef = useRef(intl);
  intlRef.current = intl;

  const [summary, setSummary] = useState(null);
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showExport, setShowExport] = useState(false);

  const { userSessionDetails } = useContext(UserSessionDetailsContext);
  // The real gate is the backend @PreAuthorize(qa.view.qc); this only hides the
  // entry point from users who would get a 403 anyway.
  const canExport = hasPermissionOrGlobalAdmin(
    userSessionDetails,
    "qa.view.qc",
  );

  const loadDashboardData = useCallback(() => {
    setLoading(true);
    setError(null);

    let completed = 0;
    const total = 2;
    let hasError = false;

    const checkDone = () => {
      completed++;
      if (completed >= total) {
        setLoading(false);
        if (!hasError) {
          setLastUpdated(new Date());
        }
      }
    };

    // Load summary data
    getFromOpenElisServer("/rest/qc/dashboard/summary", (response) => {
      if (response && (response.data || response.totalInstruments != null)) {
        setSummary(response.data || response);
      } else if (response && typeof response === "object") {
        setSummary(response);
      } else {
        hasError = true;
        setError(
          intlRef.current.formatMessage({
            id: "qc.dashboard.error.loadFailed",
          }),
        );
      }
      checkDone();
    });

    // Load instruments data
    getFromOpenElisServer("/rest/qc/dashboard/instruments", (response) => {
      if (response && response.data) {
        setInstruments(response.data || []);
      } else if (Array.isArray(response)) {
        setInstruments(response);
      } else {
        hasError = true;
        setError(
          intlRef.current.formatMessage({
            id: "qc.dashboard.error.loadFailed",
          }),
        );
      }
      checkDone();
    });
  }, []);

  useEffect(() => {
    loadDashboardData();
    const intervalId = setInterval(loadDashboardData, REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [loadDashboardData]);

  const handleRefresh = () => {
    loadDashboardData();
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return "";
    return intl.formatDate(lastUpdated, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  if (loading && !summary && instruments.length === 0) {
    return (
      <div className="qc-dashboard-loading" data-testid="qc-dashboard-loading">
        <Loading
          description={intl.formatMessage({ id: "qc.dashboard.loading" })}
          withOverlay={false}
        />
      </div>
    );
  }

  return (
    <div className="qc-dashboard" data-testid="qc-dashboard">
      {/* Header */}
      <div className="qc-dashboard-header" data-testid="qc-dashboard-header">
        <div className="qc-dashboard-header-title">
          <PageBreadCrumb
            breadcrumbs={[
              { label: "home.label", link: "/" },
              { label: "analyzer.page.hierarchy.root", link: "/analyzers" },
              { label: "qc.dashboard.title", link: "" },
            ]}
          />
          <PageTitle
            breadcrumbs={[
              {
                label: intl.formatMessage({
                  id: "analyzer.page.hierarchy.root",
                }),
                link: "/analyzers",
              },
              {
                label: intl.formatMessage({ id: "qc.dashboard.title" }),
              },
            ]}
            subtitle={intl.formatMessage({ id: "qc.dashboard.subtitle" })}
          />
        </div>
        <div className="qc-dashboard-header-actions">
          <span
            className="qc-dashboard-last-updated"
            data-testid="qc-dashboard-last-updated"
          >
            {intl.formatMessage({ id: "qc.dashboard.lastUpdated" })}:{" "}
            {formatLastUpdated()}
          </span>
          {canExport && (
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Download}
              onClick={() => setShowExport(true)}
              data-testid="qc-dashboard-export-button"
            >
              {intl.formatMessage({ id: "reports.export" })}
            </Button>
          )}
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Renew}
            iconDescription={intl.formatMessage({
              id: "qc.dashboard.refresh",
            })}
            onClick={handleRefresh}
            data-testid="qc-dashboard-refresh-button"
          >
            {intl.formatMessage({ id: "qc.dashboard.refresh" })}
          </Button>
        </div>
      </div>

      {/* Error notification */}
      {error && (
        <InlineNotification
          kind="error"
          title={intl.formatMessage({ id: "qc.dashboard.error.title" })}
          subtitle={error}
          onClose={() => setError(null)}
          data-testid="qc-dashboard-error"
        />
      )}

      {/* Active Violations Banner */}
      <ActiveViolationsBanner refreshSignal={lastUpdated} />

      {/* Summary Tiles */}
      <QCSummaryTiles summary={summary || {}} loading={loading} />

      {/* Tabbed Content */}
      <Tabs defaultSelectedIndex={initialTab}>
        <TabList contained aria-label="QC Dashboard tabs">
          <Tab data-testid="qc-tab-instruments">
            {intl.formatMessage({ id: "qc.dashboard.tab.instruments" })}
          </Tab>
          <Tab data-testid="qc-tab-alerts">
            {intl.formatMessage({ id: "qc.dashboard.tab.alerts" })}
          </Tab>
          <Tab data-testid="qc-tab-bench">
            {intl.formatMessage({ id: "qc.dashboard.tab.bench" })}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <InstrumentsTab instruments={instruments} loading={loading} />
          </TabPanel>
          <TabPanel>
            <AlertsTab />
          </TabPanel>
          <TabPanel>
            <BenchQcTab />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <QCExportModal
        open={showExport}
        onClose={() => setShowExport(false)}
        instruments={instruments}
      />
    </div>
  );
};

export default QCDashboard;
